import { useState } from 'react';
import {
  Shield, Plus, ChevronDown, ChevronRight, GripVertical, Eye,
  Edit3, Trash2, Copy, Save, FileText, AlertTriangle, Star,
  Hash, Calendar, CheckSquare, Camera, PenTool, MapPin, ListOrdered
} from 'lucide-react';
import { mockQuestionnaire } from '../../data/mockData';

const questionTypeIcons: Record<string, React.ElementType> = {
  text: Edit3, number: Hash, date: Calendar, yes_no: CheckSquare,
  multiple_choice: ListOrdered, rating: Star, photo: Camera, signature: PenTool, gps: MapPin,
};

export default function QuestionnairePage() {
  const [expandedSection, setExpandedSection] = useState<string | null>('sec-001');
  const questionnaire = mockQuestionnaire;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Questionnaire Builder</h1>
          <p className="text-sm text-slate-500 mt-1">Design and manage RISQ inspection questionnaires</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-2">
            <FileText className="w-4 h-4" /> Import Excel
          </button>
          <button className="px-4 py-2 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 flex items-center gap-2 shadow-md shadow-sky-500/20">
            <Plus className="w-4 h-4" /> New Questionnaire
          </button>
        </div>
      </div>

      {/* Current Questionnaire */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center">
                <Shield className="w-7 h-7 text-sky-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">{questionnaire.name}</h2>
                <p className="text-sm text-slate-500 mt-1">{questionnaire.description}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                  <span>Version {questionnaire.version}</span>
                  <span>•</span>
                  <span>{questionnaire.sections.length} sections</span>
                  <span>•</span>
                  <span>{questionnaire.totalQuestions} questions</span>
                  <span>•</span>
                  <span className="text-emerald-600 font-medium">Active</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-2">
                <Eye className="w-4 h-4" /> Preview
              </button>
              <button className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-2">
                <Copy className="w-4 h-4" /> Duplicate
              </button>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="divide-y divide-slate-100">
          {questionnaire.sections.map((section, idx) => {
            const isExpanded = expandedSection === section.id;
            return (
              <div key={section.id}>
                <button
                  onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                  className="w-full flex items-center gap-4 p-5 hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-sm font-bold text-slate-600">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-800">{section.title}</h3>
                    <p className="text-xs text-slate-400 mt-0.5">{section.description}</p>
                  </div>
                  <span className="text-xs text-slate-400 mr-2">{section.questions.length} questions</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>

                {isExpanded && (
                  <div className="px-5 pb-5 ml-12">
                    <div className="space-y-2">
                      {section.questions.map((question, qIdx) => {
                        const TypeIcon = questionTypeIcons[question.type] || Edit3;
                        return (
                          <div key={question.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors group">
                            <GripVertical className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab" />
                            <div className="w-7 h-7 bg-white border border-slate-200 rounded-lg flex items-center justify-center">
                              <TypeIcon className="w-3.5 h-3.5 text-slate-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-slate-700 truncate">{question.text}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] text-slate-400 capitalize">{question.type.replace('_', ' ')}</span>
                                {question.isRequired && (
                                  <span className="text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded font-medium">Required</span>
                                )}
                                {question.isRiskWeighted && (
                                  <span className="text-[10px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded font-medium">
                                    Risk Weight: {question.riskWeight}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                              <button className="p-1.5 rounded-lg hover:bg-white"><Edit3 className="w-3.5 h-3.5 text-slate-400" /></button>
                              <button className="p-1.5 rounded-lg hover:bg-white"><Copy className="w-3.5 h-3.5 text-slate-400" /></button>
                              <button className="p-1.5 rounded-lg hover:bg-white"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <button className="mt-3 px-4 py-2 border-2 border-dashed border-slate-200 rounded-xl text-sm text-slate-400 hover:border-sky-300 hover:text-sky-500 transition-colors flex items-center gap-2 w-full justify-center">
                      <Plus className="w-4 h-4" /> Add Question
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Add Section */}
        <div className="p-5 border-t border-slate-100">
          <button className="w-full px-4 py-3 border-2 border-dashed border-slate-200 rounded-xl text-sm text-slate-400 hover:border-sky-300 hover:text-sky-500 transition-colors flex items-center justify-center gap-2">
            <Plus className="w-5 h-5" /> Add Section
          </button>
        </div>
      </div>
    </div>
  );
}
